// Блок «Разбор ИИ» на карточке замера: ожидает / текст / не настроен / ошибка.
// «Пересоздать» и «Повторить» ставят замер в очередь (POST /api/inbody/scans/{id}/analysis);
// пока разбор ожидается, карточка сама перечитывает замер раз в несколько секунд.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button, Card, ErrorText, Icon } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { Disclaimer, Markdown, NotConfigured } from '@/pages/ai/parts'

import { inbodyApi, inbodyKeys, useInvalidateInBody, type AnalysisOut } from '../api'

export const POLL_MS = 5000

export function AnalysisCard({ scanId, analysis }: { scanId: string; analysis: AnalysisOut | null }) {
  const client = useQueryClient()
  const invalidate = useInvalidateInBody()
  // тот же ключ, что у useScan: опрос обновляет и карточку замера целиком
  useQuery({
    queryKey: inbodyKeys.scan(scanId),
    queryFn: () => inbodyApi.scan(scanId),
    refetchInterval: analysis?.status === 'pending' ? POLL_MS : false,
  })
  const request = useMutation({
    mutationFn: () => inbodyApi.requestAnalysis(scanId),
    networkMode: 'always',
    onSuccess: (next) => {
      client.setQueryData(inbodyKeys.scan(scanId), (old: { analysis: AnalysisOut | null } | undefined) =>
        old ? { ...old, analysis: next } : old,
      )
      void invalidate()
    },
  })
  const status = analysis?.status ?? null
  const busy = request.isPending || status === 'pending'

  let body
  if (status === null) {
    body = <p className="text-sm text-muted">Разбор для этого замера не запрашивался.</p>
  } else if (status === 'pending') {
    body = (
      <p className="flex items-center gap-2 text-sm text-muted">
        <Icon name="info" size={18} />
        Разбор ожидает — появится здесь через минуту-две.
      </p>
    )
  } else if (status === 'not_configured') {
    body = <NotConfigured />
  } else if (status === 'error') {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm text-danger" role="alert">
          {analysis?.error ?? 'Не удалось получить разбор.'}
        </p>
        <Button onClick={() => request.mutate()} loading={request.isPending}>
          Повторить
        </Button>
      </div>
    )
  } else {
    body = (
      <div className="flex flex-col gap-2">
        <Markdown text={analysis?.content_md ?? ''} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {analysis?.generated_at ? (
            <span className="text-xs text-muted">Сделан {formatDateTime(analysis.generated_at)}</span>
          ) : null}
          <Disclaimer />
        </div>
      </div>
    )
  }

  return (
    <Card
      title="Разбор ИИ"
      actions={
        status === 'ok' || status === null ? (
          <Button variant="ghost" onClick={() => request.mutate()} loading={busy} disabled={busy}>
            {status === 'ok' ? 'Пересоздать' : 'Запросить разбор'}
          </Button>
        ) : null
      }
    >
      {body}
      <ErrorText error={request.error} />
    </Card>
  )
}
